/**
 * <story-reviews> — Instagram-story viewer for the customer review screenshots.
 *
 * The bubbles and the (empty) viewer are rendered by the section, so this file
 * only wires up behaviour: open on a bubble, segmented progress bars that
 * auto-advance, tap left/right to move, hold to pause, swipe, arrow keys, and
 * a seen state that greys the ring out the way the app does.
 */

const SEEN_KEY = 'oos:stories-seen';
const HOLD_MS = 180;

/** @returns {Set<string>} the story sources this browser has already watched. */
function readSeen() {
  try {
    return new Set(JSON.parse(localStorage.getItem(SEEN_KEY) || '[]'));
  } catch {
    // Private windows and blocked site data both throw here; a viewer with no
    // history just sees every ring lit, which is the right fallback.
    return new Set();
  }
}

function writeSeen(seen) {
  try {
    localStorage.setItem(SEEN_KEY, JSON.stringify([...seen]));
  } catch {
    /* nothing to persist to — the in-page state still updates */
  }
}

class StoryReviews extends HTMLElement {
  #index = 0;
  #animation = null;
  #holdTimer = null;
  #railObserver = null;

  connectedCallback() {
    this.bubbles = [...this.querySelectorAll('[data-story-index]')];
    this.viewer = this.querySelector('[data-story-viewer]');

    if (!this.bubbles.length || !this.viewer) return;

    this.image = this.viewer.querySelector('[data-story-image]');
    this.caption = this.viewer.querySelector('[data-story-caption]');
    this.barTrack = this.viewer.querySelector('[data-story-bars]');
    this.duration = (Number(this.dataset.duration) || 5) * 1000;

    this.#buildBars();
    this.#paintSeen();

    this.bubbles.forEach((bubble, i) => {
      bubble.addEventListener('click', () => this.open(i));
    });

    this.#wireRail();

    this.viewer.querySelector('[data-story-close]').addEventListener('click', () => this.close());
    this.viewer.querySelector('[data-story-prev]').addEventListener('click', () => this.#step(-1));
    this.viewer.querySelector('[data-story-next]').addEventListener('click', () => this.#step(1));

    // Esc and the backdrop both close the native dialog; keep our timer in sync.
    this.viewer.addEventListener('close', () => this.#stop());
    this.viewer.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowRight') this.#step(1);
      if (event.key === 'ArrowLeft') this.#step(-1);
    });

    // Press and hold anywhere on the stage to pause, the way stories do. The
    // delay keeps an ordinary tap from registering as a hold.
    const stage = this.viewer.querySelector('.story-viewer__stage');
    stage.addEventListener('pointerdown', () => {
      this.#holdTimer = setTimeout(() => this.#animation?.pause(), HOLD_MS);
    });
    for (const type of ['pointerup', 'pointercancel', 'pointerleave']) {
      stage.addEventListener(type, () => {
        clearTimeout(this.#holdTimer);
        if (this.#animation?.playState === 'paused') this.#animation.play();
      });
    }

    let startX = null;
    stage.addEventListener('touchstart', (event) => {
      startX = event.changedTouches[0].clientX;
    }, { passive: true });
    stage.addEventListener('touchend', (event) => {
      if (startX === null) return;
      const dx = event.changedTouches[0].clientX - startX;
      startX = null;
      if (Math.abs(dx) > 60) this.#step(dx < 0 ? 1 : -1);
    }, { passive: true });
  }

  disconnectedCallback() {
    this.#stop();
    this.#railObserver?.disconnect();
  }

  /**
   * Wires the rail's arrows. Each only appears when there is actually somewhere
   * to scroll that way, so a short rail on a wide screen stays clean.
   */
  #wireRail() {
    const rail = this.querySelector('[data-story-rail]');

    if (!rail) return;

    const buttons = [...this.querySelectorAll('[data-story-scroll]')];
    const next = this.querySelector('[data-story-scroll="1"]');

    const update = () => {
      // A sub-pixel rounding slack, or the forward arrow never quite goes away
      // at the end of the rail.
      const remaining = rail.scrollWidth - rail.clientWidth - Math.abs(rail.scrollLeft);
      const canBack = Math.abs(rail.scrollLeft) > 2;
      const canForward = remaining > 2;

      for (const button of buttons) {
        button.hidden = Number(button.dataset.storyScroll) < 0 ? !canBack : !canForward;
      }
    };

    for (const button of buttons) {
      button.addEventListener('click', () => {
        const direction = Number(button.dataset.storyScroll);
        const card = rail.querySelector('.story-card');
        const gap = Number.parseFloat(getComputedStyle(rail).columnGap) || 0;
        const step = card ? (card.getBoundingClientRect().width + gap) * 2 : rail.clientWidth * 0.8;

        rail.scrollBy({ left: step * direction, behavior: 'smooth' });
      });
    }

    rail.addEventListener('scroll', update, { passive: true });
    this.#railObserver = new ResizeObserver(update);
    this.#railObserver.observe(rail);
    update();

    // Nudge the forward arrow once, the first time the rail is on screen.
    if (next) {
      const seen = new IntersectionObserver((entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting || next.hidden) continue;

          next.dataset.nudge = 'true';
          seen.disconnect();
        }
      }, { threshold: 0.4 });
      seen.observe(rail);
    }
  }

  #buildBars() {
    this.barTrack.replaceChildren(
      ...this.bubbles.map(() => {
        const bar = document.createElement('div');
        bar.className = 'story-viewer__bar';
        const fill = document.createElement('span');
        fill.className = 'story-viewer__bar-fill';
        bar.append(fill);
        return bar;
      })
    );
    this.bars = [...this.barTrack.children];
  }

  #paintSeen() {
    const seen = readSeen();
    for (const bubble of this.bubbles) {
      if (seen.has(bubble.dataset.storySrc)) bubble.dataset.seen = 'true';
    }
  }

  /** @param {number} index - Story to open the viewer on. */
  open(index) {
    this.viewer.showModal();
    this.#show(index);
  }

  close() {
    // The 'close' event handler stops the timer.
    if (this.viewer.open) this.viewer.close();
  }

  #stop() {
    this.#animation?.cancel();
    this.#animation = null;
    clearTimeout(this.#holdTimer);
  }

  /** @param {number} delta - -1 for back, 1 for forward. */
  #step(delta) {
    const next = this.#index + delta;

    if (next < 0) return;
    if (next >= this.bubbles.length) {
      this.close();
      return;
    }

    this.#show(next);
  }

  /** @param {number} index - Story to render. */
  #show(index) {
    this.#stop();
    this.#index = index;

    const bubble = this.bubbles[index];
    const src = bubble.dataset.storySrc;

    this.image.src = src;
    this.image.alt = bubble.dataset.storyAlt || '';
    this.caption.textContent = bubble.dataset.storyLabel || '';

    this.bars.forEach((bar, i) => {
      bar.dataset.state = i < index ? 'done' : 'pending';
      if (i !== index) bar.firstElementChild.style.transform = i < index ? 'scaleX(1)' : 'scaleX(0)';
    });

    const fill = this.bars[index].firstElementChild;
    fill.style.transform = 'scaleX(0)';
    this.#animation = fill.animate(
      [{ transform: 'scaleX(0)' }, { transform: 'scaleX(1)' }],
      { duration: this.duration, easing: 'linear', fill: 'forwards' }
    );
    this.#animation.onfinish = () => this.#step(1);

    const seen = readSeen();
    seen.add(src);
    writeSeen(seen);
    bubble.dataset.seen = 'true';

    // Warm the next image so the swap doesn't flash.
    const upcoming = this.bubbles[index + 1];
    if (upcoming) new Image().src = upcoming.dataset.storySrc;
  }
}

if (!customElements.get('story-reviews')) {
  customElements.define('story-reviews', StoryReviews);
}
